/**
 * Whether a height flush re-pins a chat thread to its last message.
 *
 * `autoScrollEnabled` is the thread's "the reader is at the bottom" state: the
 * last scroll ended within the threshold of the end and was not moving up.
 * While it holds, the bottom is where the view belongs, so a height change
 * re-pins in the same flush, before the engine drains.
 *
 * `userScrolling` does not veto the pin. It is set by EVERY scroll event for
 * 150ms, including the one the thread's own `scrollToBottom` fires. When the
 * last message shrank inside that window (a markdown turn re-measuring
 * 2081px -> 607px at boot on /dev/assets/chat), the old
 * `autoScrollEnabled && !userScrolling` gate skipped the pin. The viewport was
 * left past the end of the list, the engine's coverage invariant logged
 * `scroll/coverage-repair/stuck` (nothing exists past `maxIndex` to render),
 * and the browser's clamp then read as an upward scroll that switched
 * auto-scroll off. A reader scrolling UP has already cleared
 * `autoScrollEnabled`, so dropping the veto never pulls them down.
 */
export function repinsAfterHeightFlush(state: {
  readonly autoScrollEnabled: boolean;
  readonly userScrolling: boolean;
}): boolean {
  return state.autoScrollEnabled;
}

/**
 * How much one height report moves the end of the list before the thread's
 * next flush lays it out.
 *
 * The distance the thread judges a scroll event against is the scroller's
 * logical height: the laid-out offset of the last message plus the last
 * message's height. The last message's height is read live from
 * `measuredHeights`, so a change to it counts at once. Every other message
 * only moves the last message's offset when `flushHeights` hands the change to
 * the engine (`invalidateHeight`). Until then the logical height still holds
 * that message's previous height, or the bind estimate if it had none.
 *
 * chat-playground (candidate.12, rc 2026-09-24 20:37:58.535Z): 110ms after the
 * ChatGPT look swap, index 200 of 203 re-measured 14054px -> 12542px. A scroll
 * event came in before the flush that would lay that out. It moved the view up
 * (47571 -> 37834) and measured 1322px short of an end that was still 1512px
 * too far down, so the view really ended 190px past the end of the list.
 * `pinnedAfterScroll` read "moving up, 1322px from the end" and unpinned. The
 * flush then anchored instead of re-pinning, the list's end moved up under a
 * view that sat inside index 200, and the engine reported
 * `scroll/coverage-repair/stuck gen=0 uncoveredPx=88 attempts=1`. candidate.11
 * took the same path (13919 -> 12542, 1276px short, really 101px past) but its
 * one repair happened to cover the gap.
 *
 * The thread adds up these deltas between flushes and judges the scroll
 * event's distance with {@link judgedDistanceFromBottomPx}.
 */
export function unflushedEndShiftPx(change: {
  readonly index: number;
  readonly lastIndex: number;
  readonly prev: number | null;
  readonly height: number;
  readonly estimatePx: number;
}): number {
  if (change.index >= change.lastIndex) return 0;
  return change.height - (change.prev ?? change.estimatePx);
}

/**
 * The distance to the end of the list that a scroll event is judged by: the
 * nearer of the laid-out end and the measured end.
 *
 * `laidOutPx` is the distance to the end the engine has laid out (the
 * scroller's logical height minus the view's bottom edge). `unflushedShiftPx`
 * is the sum of {@link unflushedEndShiftPx} since the last flush, so the
 * measured end is `laidOutPx + unflushedShiftPx` away.
 *
 * Either end is at the bottom. A view at or past the laid-out end is at the
 * bottom of everything the browser can scroll to; a view at or past the
 * measured end is at the bottom of the list as the next flush will lay it
 * out. A reader who scrolled up ends above both. So the unflushed shift may
 * only bring the end closer (a pending shrink), never push it further away (a
 * pending growth): the content a pending growth adds is not laid out yet, and
 * a pinned thread's flush follows it down.
 *
 * chat-playground (candidate.14, rc 2026-09-24 22:05:24.339Z): first measures
 * after the rich-then-plain step's look switch. A pinned flush shrank index
 * 190 (21862px -> 16544px) and re-pinned, which moves the view UP to the new
 * end (39633 -> 30889). Before that scroll event was judged, index 190
 * re-measured larger (16544px -> 17983px; index 192 291px -> 114px): +1262px
 * unflushed. The event ended 57px past the laid-out end, pinned by the c9
 * rule, but the
 * round-10 correction added the whole +1262px and read "moving up, 1205px
 * from the end": `chat/autoScroll enabled=false distanceFromBottom=1205`. The
 * flushes anchored from then on, the Rich -> Plain rebind opened the thread
 * at message 0, and the spec failed with "plain renderer lost the prose
 * text".
 */
export function judgedDistanceFromBottomPx(distance: {
  readonly laidOutPx: number;
  readonly unflushedShiftPx: number;
}): number {
  return distance.laidOutPx + Math.min(0, distance.unflushedShiftPx);
}

/** Within this many px of the end, a scroll that is not moving up re-arms
 *  auto-scroll. */
export const AUTO_SCROLL_THRESHOLD_PX = 100;

/**
 * Whether a thread is pinned to its last message after one scroll event.
 *
 * - The view ends at or past the end of the list (`distanceFromBottom <= 1`):
 *   pinned, whichever way it moved. Only the thread's own layout gets the
 *   view there going UP: the browser clamping scrollTop when the content
 *   shrinks, or the thread's own re-pin to a shorter last message. A reader
 *   scrolling up from the bottom always ends above it.
 * - Otherwise moving up: not pinned. The reader left the bottom.
 * - Otherwise (not moving up) and already pinned: still pinned. Moving down
 *   never means the reader left the bottom.
 * - Otherwise: pinned when within {@link AUTO_SCROLL_THRESHOLD_PX} of the end.
 *
 * chat-playground (candidate.10, rc 2026-09-24 10:36:16.128Z): the thread
 * was pinned, and its own reveal of the last message scrolled the view DOWN
 * (39632 -> 59155). While that happened the rows below re-measured (index
 * 200 12542 -> 13929px, index 202 first measured at 6083px), so the view
 * landed 1145px short of the new end. The previous rule judged that
 * downward scroll against the 100px threshold and switched auto-scroll off.
 * The next rebind (the Rich -> Plain switch's `setMessages`) then opened
 * the thread at message 0 instead of the bottom. The prose the spec had
 * sampled from the last message was no longer mounted, and the spec failed
 * with "plain renderer lost the prose text".
 *
 * chat-playground (c9, rc 2026-09-24 08:55:32Z): the last message measured
 * 2380px and then 968px while pinned. Before the next height flush a scroll
 * event came in with the view still past the new end, and it read as an
 * upward scroll. The old rule (any upward scroll clears auto-scroll) turned
 * the pin off, so the flush anchored instead of re-pinning, and the engine
 * reported `scroll/coverage-repair/stuck gen=0 uncoveredPx=558`.
 *
 * `distanceFromBottom` is the list's logical height minus the view's bottom
 * edge: negative when the view ends past the end. `wasPinned` is the
 * thread's pin state before this scroll event.
 */
export function pinnedAfterScroll(scroll: {
  readonly prevTop: number;
  readonly top: number;
  readonly distanceFromBottom: number;
  readonly wasPinned: boolean;
}): boolean {
  if (scroll.distanceFromBottom <= 1) return true;
  if (scroll.top < scroll.prevTop) return false;
  if (scroll.wasPinned) return true;
  return scroll.distanceFromBottom < AUTO_SCROLL_THRESHOLD_PX;
}
